import type { FHIRModel } from '@ctw/shared/api/fhir/models/fhir-model';
import {
  OverviewCard,
  type OverviewCardProps,
} from '@ctw/shared/components/containers/overview-card';
import type {
  LegacyMinRecordItem,
  LegacyTableProps,
} from '@ctw/shared/components/tables/legacy-table';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import type { Resource } from 'fhir/r4';
import { isFunction } from 'lodash-es';
import type { ReactElement } from 'react';
import { useToggleRead } from '../hooks/use-toggle-read';

export interface SpecificResourceOverviewCardProps {
  onSeeAllResources: () => void;
  isInPatientSummary?: boolean;
}
export interface ResourceOverviewCardProps<T extends LegacyMinRecordItem>
  extends Omit<OverviewCardProps, 'children' | 'empty'> {
  renderResource: (record: T, index: number) => ReactElement<{ record: T; index?: number }>;
  data: Array<T>;
  onRowClick?: LegacyTableProps<T>['handleRowClick'];
  rowClassName?: ClassName;
}

export const ResourceOverviewCard = <T extends Resource, M extends FHIRModel<T>>({
  renderResource,
  data = [],
  onRowClick,
  rowClassName,
  footerCTA,
  ...overviewCardProps
}: ResourceOverviewCardProps<M>) => {
  const telemetry = useTelemetry();
  const { requestContext } = useCTW();
  const { toggleRead } = useToggleRead();

  function handleRowClick(record: M) {
    if (onRowClick) {
      if (!(record.isRead || record.ownedByBuilder(requestContext.builderId))) {
        void toggleRead(record);
      }
      onRowClick({ telemetry, record });
      telemetry.trackInteraction('table_row_left_click');
    }
  }

  return (
    <OverviewCard
      {...overviewCardProps}
      empty={data.length === 0}
      fullbleed={true}
      footerCTA={
        footerCTA
          ? {
              label: footerCTA.label,
              onClick: () => {
                footerCTA.onClick();
              },
            }
          : undefined
      }
    >
      <ul className={tw`flex h-full w-full flex-col divide-y divide-border-main`}>
        {data.map((record, index) => (
          <li key={record.key}>
            <button
              type="button"
              data-testid="resource-overview-row"
              className={twx(
                'grouped group relative h-full w-full px-3 py-1.5 text-left -outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-main',
                rowClassName,
                {
                  'cursor-pointer hover:bg-background-hover': isFunction(onRowClick),
                  'last:rounded-b-lg': !footerCTA,
                },
              )}
              onClick={(event) => {
                event.stopPropagation();
                handleRowClick(record);
              }}
            >
              {renderResource(record, index)}
            </button>
          </li>
        ))}
      </ul>
    </OverviewCard>
  );
};
